import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Assets — original Inner Hustle imagery */

/* ---------- CONTENT FLAGS ----------
 * Flip ISSUE_CONTENT_LIVE to true when Issue 001 · Becoming Her articles are ready.
 * Until then, all article sections on the homepage and individual story pages
 * display a Coming Soon placeholder.
 */
export const ISSUE_CONTENT_LIVE = false;

const ASSETS = {
  hero: "/hero-edited.png",
  interlude: "https://customer-assets.emergentagent.com/job_feed-to-page/artifacts/f3dv7yb9_image.png",
  quietPower: "https://customer-assets.emergentagent.com/job_feed-to-page/artifacts/oxcdo3g6_image.png",
  realHustle: "https://customer-assets.emergentagent.com/job_feed-to-page/artifacts/j6z4rn7q_image.png",
  passport: "/passport.png",
  inRotation: "/in-rotation/sade-cover.png",
  listeningRoom: "https://customer-assets.emergentagent.com/job_feed-to-page/artifacts/obpy6de9_image.png",
};

const STORIES = [
  { num: "02", chapter: "Feature",  title: "The Real Hustle", anchor: "/story/real-hustle" },
  { num: "06", chapter: "Travel",   title: "Passport to Peace", anchor: "/story/passport-to-peace" },
  { num: "10", chapter: "Conversation",  title: "Sixth Sense", anchor: "/story/sixth-sense" },
  { num: "14", chapter: "Sound",    title: "In Rotation", anchor: "/story/in-rotation" },
  { num: "18", chapter: "Culture",  title: "The Listening Room", anchor: "#story-18" },
];

const MARQUEE = ISSUE_CONTENT_LIVE
  ? [
      "Becoming Her",
      "The Real Hustle",
      "Passport to Peace",
      "Sixth Sense",
      "In Rotation",
      "The Listening Room",
      "Move Differently",
    ]
  : [
      "Becoming Her",
      "Coming Soon",
      "Issue 001",
      "Move Differently",
      "Stay Close",
    ];

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ---------- NAV ---------- */
const Nav = () => {
  const [visible, setVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.85);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    // Lock body scroll when mobile menu is open
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);
  const closeMenu = () => setMenuOpen(false);
  return (
    <>
    <nav
      data-testid="ih-nav"
      aria-hidden={!visible}
      className={`fixed top-0 left-0 right-0 z-30 ih-nav-sticky transition-all duration-500 ${
        visible
          ? "is-scrolled opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 -translate-y-4 pointer-events-none"
      }`}
    >
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-3 md:py-4 flex items-center justify-between text-[var(--ih-cream)]">
      <a href="#top" data-testid="ih-logo" className="flex items-center gap-3 group">
        <img
          src="/ih-logo.png"
          alt="IH"
          className="h-9 md:h-12 w-auto pointer-events-none select-none"
        />
        <span className="hidden sm:inline font-mono-ui text-[10px] uppercase tracking-[0.32em]">
          The Inner Hustle
        </span>
      </a>

      <div className="hidden md:flex items-center gap-10 font-mono-ui text-[10px] uppercase tracking-[0.28em]">
        <a href="#issue" className="ih-link" data-testid="nav-issues">Issues</a>
        <a href="#featured" className="ih-link" data-testid="nav-stories">Stories</a>
        <a href="/archive" className="ih-link" data-testid="nav-archive">Archive</a>
        <a href="#story-01" className="ih-link" data-testid="nav-about">About</a>
        <a href="#subscribe" className="ih-link" data-testid="nav-subscribe">Stay Close</a>
      </div>

      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        className="md:hidden p-2 -mr-2 text-[var(--ih-cream)]"
        aria-label="Open menu"
        data-testid="mobile-menu-toggle"
      >
        <span className="block w-6 h-[1.5px] bg-current mb-[6px]" />
        <span className="block w-6 h-[1.5px] bg-current mb-[6px]" />
        <span className="block w-4 h-[1.5px] bg-current ml-auto" />
      </button>
    </div>
  </nav>

  {/* Mobile drawer */}
  {menuOpen && (
    <div
      className="fixed inset-0 z-50 bg-[var(--ih-ink)] text-[var(--ih-cream)] md:hidden"
      data-testid="mobile-menu-drawer"
    >
      <div className="flex items-center justify-between px-5 py-4">
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.32em]">The Inner Hustle</span>
        <button type="button" onClick={closeMenu} aria-label="Close menu" className="p-2 -mr-2" data-testid="mobile-menu-close">
          <span className="block relative w-5 h-5">
            <span className="absolute inset-0 top-1/2 -translate-y-1/2 h-[1.5px] bg-current rotate-45" />
            <span className="absolute inset-0 top-1/2 -translate-y-1/2 h-[1.5px] bg-current -rotate-45" />
          </span>
        </button>
      </div>
      <div className="px-8 pt-10 pb-12 flex flex-col gap-7 font-marquee text-[2rem] leading-[1.0]">
        <a href="#issue" onClick={closeMenu}>Issues</a>
        <a href="#featured" onClick={closeMenu}>Stories</a>
        <a href="/archive" onClick={closeMenu} data-testid="mobile-nav-archive">Archive</a>
        <a href="#story-01" onClick={closeMenu}>About</a>
        <a href="#subscribe" onClick={closeMenu}>Stay Close</a>
      </div>
      <div className="px-8 pt-6 mt-auto border-t border-[var(--ih-cream)]/15">
        <div className="pt-6 font-mono-ui text-[9px] uppercase tracking-[0.42em] opacity-60">
          Issue 001 · Becoming Her
        </div>
      </div>
    </div>
  )}
  </>
  );
};

/* ---------- HERO (text-only Coming Soon cover) ---------- */
const Hero = () => (
  <section
    id="top"
    className="relative h-[100vh] min-h-[640px] w-full overflow-hidden bg-[var(--ih-ink)] text-[var(--ih-cream)] flex items-center justify-center"
    data-testid="hero-coming-soon"
  >
    {/* Subtle grain / vignette so it doesn't feel flat */}
    <div
      className="absolute inset-0 pointer-events-none opacity-40"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.4) 100%)",
      }}
      aria-hidden="true"
    />
    <div className="relative max-w-[1000px] mx-auto px-6 md:px-10 text-center">
      {/* pink accent dash */}
      <div
        className="w-10 md:w-14 h-[2px] mx-auto mb-10 md:mb-14"
        style={{ background: "var(--ih-accent)" }}
        aria-hidden="true"
      />

      {/* Kicker */}
      <div
        className="font-mono-ui uppercase mb-8 md:mb-10"
        style={{
          fontSize: "clamp(0.72rem, 0.95vw, 0.86rem)",
          letterSpacing: "0.42em",
          color: "var(--ih-accent)",
        }}
      >
        Issue 001
      </div>

      {/* Title — big serif */}
      <h1
        data-testid="hero-title"
        className="font-marquee leading-[0.95] tracking-[-0.01em]"
        style={{ fontSize: "clamp(3rem, 9vw, 8rem)" }}
      >
        Becoming Her
      </h1>

      {/* Coming soon */}
      <div
        className="font-mono-ui uppercase mt-10 md:mt-14"
        style={{
          fontSize: "clamp(0.75rem, 1vw, 0.95rem)",
          letterSpacing: "0.5em",
          color: "var(--ih-cream)",
          opacity: 0.75,
        }}
      >
        Coming Soon
      </div>

      {/* subscribe CTA to make the coming-soon page useful */}
      <a
        href="#subscribe"
        className="mt-16 md:mt-20 inline-flex items-center gap-2 font-mono-ui uppercase border-b pb-1 hover:gap-3 transition-all"
        style={{
          fontSize: "clamp(0.7rem, 0.85vw, 0.8rem)",
          letterSpacing: "0.42em",
          borderColor: "var(--ih-accent)",
          color: "var(--ih-cream)",
        }}
        data-testid="hero-coming-soon-cta"
      >
        Be the first to read it
        <span aria-hidden>→</span>
      </a>
    </div>
  </section>
);

/* ---------- MARQUEE (black bg, pink dots) ---------- */
const Marquee = () => (
  <section className="bg-[var(--ih-ink)] text-[var(--ih-cream)] py-6 overflow-hidden">
    <div className="ih-marquee-track">
      {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((label, i) => (
        <span
          key={i}
          className="font-marquee uppercase text-[1.4rem] md:text-[1.7rem] mx-10 whitespace-nowrap tracking-[0.22em]"
        >
          {label}
          <span className="inline-block mx-8 align-middle w-[10px] h-[10px] rounded-full" style={{ background: "var(--ih-accent)" }} />
        </span>
      ))}
    </div>
  </section>
);

/* ---------- MANIFESTO (full-bleed black, calligraphic italic) ---------- */
const Manifesto = () => (
  <section
    id="manifesto"
    className="relative bg-[var(--ih-ink)] text-[var(--ih-cream)] py-32 md:py-44 overflow-hidden"
    data-reveal
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
      {/* Pink accent dash — left-aligned like the other section markers */}
      <div className="w-12 md:w-16 h-[2px] mb-10" style={{ background: "var(--ih-accent)" }} aria-hidden="true" />

      <h2
        className="font-manifesto leading-[1.05] tracking-[-0.005em]"
        style={{ fontSize: "clamp(2.6rem, 8.4vw, 8.4rem)" }}
        data-testid="manifesto-title"
      >
        <span className="block">Soft is not surrender.</span>
        <span className="block mt-2 md:mt-4">Quiet is not absence.</span>
        <span
          className="block mt-2 md:mt-4"
          style={{ color: "var(--ih-accent)" }}
        >
          Rest is not retreat.
        </span>
      </h2>

      <div className="mt-20 md:mt-28 max-w-3xl mx-auto space-y-10">
        <p className="text-[1.05rem] md:text-[1.15rem] leading-[1.7] text-[var(--ih-cream)]/75">
          The Inner Hustle is for the ones moving differently. Not louder.
          Not faster. Just on their own frequency.
        </p>
        <p className="font-manifesto text-[1.7rem] md:text-[2.1rem] leading-[1.25] text-[var(--ih-cream)]">
          You don't have to match the room.
        </p>
      </div>
    </div>
  </section>
);

/* ---------- EDITOR'S NOTE ---------- */
const EditorNote = () => (
  <section id="story-01" className="py-24 md:py-32 bg-[var(--ih-paper)]" data-reveal>
    <div className="max-w-[1100px] mx-auto px-6 md:px-10">
      <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)] mb-3">
        Editor's Note · Issue 001
      </div>
      <h2
        className="font-display font-light leading-[1.0] tracking-[-0.02em]"
        style={{ fontSize: "clamp(2.4rem, 5.5vw, 5rem)" }}
        data-testid="editor-title"
      >
        Quiet <span className="italic" style={{ color: "var(--ih-accent)" }}>Power.</span>
      </h2>
      <div className="ih-rule my-12 max-w-[5rem]" />

      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 text-[1.08rem] leading-[1.75] text-[var(--ih-ink-soft)] space-y-6">
          <p className="ih-dropcap">
            Power doesn't always arrive loudly. Sometimes it shows up as a
            feeling you can't quite explain. A quiet sense that the way you've
            been moving no longer fits the way you feel. Not because you've
            failed. Not because you've fallen apart. Just because something is
            shifting.
          </p>
          <p>This issue lives in that space.</p>
          <p>
            Becoming Her is about the moments before clarity. The moments when
            you begin asking different questions. When what once worked no
            longer feels quite right, but you aren't sure what comes next.
          </p>
          <p>
            For many women, strength has meant pushing through. Holding things
            together. Showing up. Meeting expectations. Continuing forward.
          </p>
          <p>
            And sometimes we do it so well that we stop noticing what it costs.
          </p>
        </div>

        <aside className="md:col-span-5 md:pl-10 md:border-l border-[var(--ih-ink)]/15">
          <p className="font-display italic text-[1.4rem] leading-[1.4] text-[var(--ih-ink)]">
            "This issue isn't about fixing that. It isn't about having the
            answers. It's about paying attention."
          </p>
          <div className="mt-10 space-y-4 text-[0.98rem] leading-[1.7] text-[var(--ih-ink-soft)]">
            <p>
              The stories and conversations in these pages explore ambition,
              grief, intuition, memory, culture, and identity.
            </p>
            <p>
              The Inner Hustle exists for women who feel something changing
              before they have words for it.
            </p>
            <p>If that's where you find yourself, welcome. You're not alone.</p>
          </div>
          <div className="mt-10 font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
            — Laurie, Editor
          </div>
        </aside>
      </div>
    </div>
  </section>
);

/* ---------- INTERLUDE ---------- */
const Interlude = () => (
  <section className="relative" data-reveal>
    <div className="relative h-[60vh] md:h-[80vh] overflow-hidden bg-black">
      <img
        src={ASSETS.interlude}
        alt="An interlude — the pause between chapters"
        className="absolute inset-0 w-full h-full object-cover object-[50%_20%]"
      />
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 100%)",
        }}
      />
      <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 text-[var(--ih-cream)]">
        <div className="w-12 md:w-16 h-[2px] mb-6" style={{ background: "var(--ih-accent)" }} aria-hidden="true" />
        <p className="font-manifesto leading-[1.1] max-w-3xl" style={{ fontSize: "clamp(2rem, 4.6vw, 4.2rem)" }}>
          The pause is part of the work.
        </p>
      </div>
    </div>
  </section>
);

/* ---------- SIXTH SENSE TEACHER CARD (text-only, paired with Quiet Power) ---------- */
const SixthSenseCard = () => (
  <a
    href="/story/sixth-sense"
    className="ih-card block group h-full"
    data-testid="story-sixth-sense"
  >
    <div className="relative aspect-square bg-[var(--ih-cream)] border border-[var(--ih-ink)]/12 p-7 md:p-9 flex flex-col">
      <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-muted)] self-end">
        Issue 001
      </div>
      <h3 className="mt-10 font-display font-light text-[1.55rem] md:text-[1.85rem] leading-[1.05] tracking-[-0.01em]">
        Sixth Sense
      </h3>
      <p className="mt-4 font-display text-[1.05rem] md:text-[1.2rem] leading-[1.45] text-[var(--ih-ink-soft)]">
        Conversations with experts in mental health and well-being.
      </p>

      <div className="mt-8">
        <p className="font-display italic text-[1.05rem] md:text-[1.15rem] leading-[1.35] text-[var(--ih-ink)]">
          featuring <span style={{ fontStyle: "normal", fontWeight: 500 }}>Dr. Michelle Frank, PhD</span>
        </p>
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)] mt-1">
          Licensed Psychologist
        </div>
      </div>

      <div className="mt-auto pt-6 font-mono-ui text-[10px] uppercase tracking-[0.32em] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
        Read <span style={{ color: "var(--ih-accent)" }}>→</span>
      </div>
    </div>
  </a>
);

/* ---------- FEATURE CARD ---------- */
const FeatureCard = ({ image, imageCaption, kicker, title, dek, num, testid, aspect = "aspect-[4/5]", imageStyle }) => (
  <a href={typeof num === "string" && num.includes("-") ? `/story/${num}` : `#story-${num}`} className="ih-card block group" data-testid={testid}>
    <div className={`relative ih-card-img-wrap ${aspect} mb-5 bg-black overflow-hidden`}>
      <img src={image} alt={title} className="ih-img ih-card-img ih-img--portrait" style={imageStyle} />
      {imageCaption && (
        <>
          <div
            className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)" }}
          />
          <div
            className="absolute left-5 bottom-5 font-mono-ui text-[10px] md:text-[11px] uppercase tracking-[0.32em]"
            style={{ color: "var(--ih-accent)" }}
          >
            {imageCaption}
          </div>
        </>
      )}
    </div>
    <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)] mb-2">
      {kicker}
    </div>
    <h3 className="font-display font-light text-[1.55rem] md:text-[1.85rem] leading-[1.05] tracking-[-0.01em]">
      {title}
    </h3>
    {dek && (
      <p className="mt-2 text-[0.98rem] leading-[1.55] text-[var(--ih-ink-soft)]">{dek}</p>
    )}
    <div className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.32em] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
      Read <span style={{ color: "var(--ih-accent)" }}>→</span>
    </div>
  </a>
);

/* ---------- FEATURED ---------- */
const Featured = () => (
  <section id="featured" className="pt-16 md:pt-20 pb-28 md:pb-36" data-reveal>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="w-12 md:w-16 h-[2px] mb-10" style={{ background: "var(--ih-accent)" }} aria-hidden="true" />
      <div className="flex items-end justify-between mb-14 gap-6">
        <div>
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)] mb-3">
            Featured · Issue 001
          </div>
          <h2
            className="font-marquee leading-[1.0] tracking-[-0.005em]"
            style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.4rem)", fontStyle: "normal", fontWeight: 500, fontOpticalSizing: "auto" }}
            data-testid="featured-title"
          >
            Stories from <span className="italic" style={{ color: "var(--ih-accent)" }}>the inside.</span>
          </h2>
        </div>
        <a href="#issue" className="ih-link font-mono-ui text-[10px] uppercase tracking-[0.32em] hidden md:inline-block">
          View Full Index →
        </a>
      </div>

      <div className="grid grid-cols-12 gap-x-8 md:gap-x-10 gap-y-6 md:gap-y-8">
        <div className="col-span-12 md:col-span-7">
          <FeatureCard image={ASSETS.quietPower} kicker="Issue 001 · Cover Essay" title="Becoming Her" dek="Where awareness begins." num="01" testid="story-becoming-her" />
        </div>
        <div className="col-span-12 md:col-span-5 flex flex-col gap-8 md:gap-10">
          <FeatureCard image={ASSETS.realHustle} kicker="Issue 001 · Feature" title="The Real Hustle" dek="The work beneath the ambition." num="real-hustle" testid="story-real-hustle" aspect="aspect-[16/9]" />
          <SixthSenseCard />
        </div>

        <div className="col-span-12 md:col-span-4">
          <FeatureCard
            image={ASSETS.passport}
            imageCaption="Barbados, West Coast"
            kicker="Issue 001 · Travel"
            title="Passport to Peace"
            dek="Returning to the places that still remember us."
            num="passport-to-peace"
            testid="story-passport"
          />
        </div>
        <div className="col-span-12 md:col-span-4">
          <FeatureCard
            image={ASSETS.inRotation}
            kicker="Issue 001 · Sound"
            title="In Rotation"
            dek="Still in rotation."
            num="in-rotation"
            testid="story-rotation"
          />
        </div>
        <div className="col-span-12 md:col-span-4">
          <a
            href="#manifesto"
            className="ih-card block group h-full"
            data-testid="story-frequency"
          >
            <div className="w-full h-full overflow-hidden">
              <img
                src="/frequency-tile.png"
                alt="On a Different Frequency — You don't have to match the room."
                className="w-full h-full"
                style={{ objectFit: "cover", objectPosition: "center" }}
              />
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- SIXTH SENSE (full story) ---------- */
const SIXTH_SENSE_QA = [
  {
    q: "What does intuition feel like?",
    a: [
      "Most people expect intuition to arrive loudly, as a clear thought, a strong opinion, or a sudden certainty. Intuition is often quiet. It shows up as a physical sensation first: tension in the chest, a tightening in the stomach, a sense of heaviness, or an urge to pause.",
      "Women are taught early to override these sensations. We learn to intellectualize discomfort, explain it away, or prioritize politeness over internal signals. Over time, this creates a disconnect between what the body notices and what the mind allows.",
    ],
  },
  {
    q: "Why are women encouraged to doubt their internal signals?",
    a: [
      "Social conditioning plays a large role. Women are often rewarded for being agreeable, accommodating, and emotionally available. Questioning a situation, especially when there's no \"logical\" proof, can be labeled as overthinking, sensitivity, or insecurity.",
      "As a result, many women learn to distrust themselves in favor of external validation. This doesn't mean intuition disappears; it means it gets buried beneath expectations, responsibilities, and self-doubt.",
    ],
  },
  {
    q: "How can someone tell the difference between intuition and anxiety?",
    a: [
      "Anxiety tends to feel urgent and repetitive. It pushes for immediate resolution and often spirals into worst-case scenarios. Intuition, on the other hand, is steady. It doesn't rush. It simply signals that something deserves attention.",
      "A helpful way to tell the difference is to notice how the message arrives:",
    ],
    pull: [
      { who: "Anxiety says,", what: "\"Something bad is about to happen.\"" },
      { who: "Intuition says,", what: "\"Something here isn't aligned.\"" },
    ],
    afterPull: "One is driven by fear. The other by awareness.",
  },
  {
    q: "What happens when women consistently ignore these signals?",
    a: [
      "Ignoring intuition often shows up later as burnout, resentment, or emotional exhaustion. Many women come into therapy not because something dramatic happened, but because they've spent years overriding themselves.",
      "Over time, the body keeps score. What starts as discomfort can turn into chronic stress, anxiety, or physical symptoms. Learning to listen earlier can prevent much deeper consequences later.",
    ],
  },
  {
    q: "How can someone begin reconnecting with their intuition?",
    a: [
      "It starts with slowing down, not dramatically, but intentionally. Paying attention to bodily responses without immediately judging or correcting them. Asking, \"What am I noticing right now?\" instead of \"What should I do?\"",
      "Reconnection doesn't happen through perfection. It happens through repetition and permission.",
    ],
  },
  {
    q: "What is one truth you wish more women trusted?",
    a: [
      "That their awareness is not something to fix or silence. It's information. When women learn to trust themselves, even when the answer is unclear, they make decisions that are more sustainable, grounded, and aligned.",
    ],
  },
];

const SixthSense = () => (
  <section id="story-10" className="bg-[var(--ih-paper)] py-24 md:py-32" data-reveal>
    <div className="max-w-[820px] mx-auto px-6 md:px-10">
      <div className="w-12 md:w-16 h-[2px] mb-8" style={{ background: "var(--ih-accent)" }} aria-hidden="true" />
      <div className="font-mono-ui text-[11px] uppercase tracking-[0.42em]" style={{ color: "var(--ih-accent)" }}>
        Issue 001 · Conversation · 10
      </div>
      <h2
        className="mt-6 font-manifesto leading-[1.0] tracking-[-0.005em]"
        style={{ fontSize: "clamp(2.6rem, 6.4vw, 6rem)" }}
        data-testid="sixthsense-title"
      >
        Sixth Sense.
      </h2>
      <p className="mt-6 font-display italic text-[1.2rem] md:text-[1.45rem] leading-[1.35] text-[var(--ih-ink)]">
        A thoughtful conversation exploring what the body and mind recognize before we do.
      </p>
      <p className="mt-6 font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-muted)]">
        Conversations with experts on the signals women are taught to ignore
      </p>
      <div className="ih-rule my-12" />

      <div className="space-y-14">
        {SIXTH_SENSE_QA.map((qa, idx) => (
          <div key={idx} className="space-y-5">
            <p
              className="font-manifesto leading-[1.2] text-[var(--ih-ink)]"
              style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)" }}
            >
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.42em] block mb-2" style={{ color: "var(--ih-accent)" }}>
                The Inner Hustle
              </span>
              {qa.q}
            </p>
            <div className="space-y-4">
              <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-muted)]">
                Dr. Michelle Frank
              </div>
              {qa.a.map((para, i) => (
                <p key={i} className="text-[1.05rem] md:text-[1.1rem] leading-[1.75] text-[var(--ih-ink-soft)]">
                  {para}
                </p>
              ))}
              {qa.pull && (
                <div className="my-6 pl-5 border-l-2" style={{ borderColor: "var(--ih-accent)" }}>
                  {qa.pull.map((line, i) => (
                    <p key={i} className="font-manifesto text-[1.25rem] md:text-[1.45rem] leading-[1.4] text-[var(--ih-ink)]">
                      <span className="opacity-70 mr-2">{line.who}</span>
                      {line.what}
                    </p>
                  ))}
                </div>
              )}
              {qa.afterPull && (
                <p className="font-manifesto text-[1.15rem] md:text-[1.3rem] leading-[1.45] text-[var(--ih-ink)]">
                  {qa.afterPull}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="ih-rule my-14" />
      <p
        className="text-center font-manifesto leading-[1.2] text-[var(--ih-ink)]"
        style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)" }}
      >
        Intuition doesn't demand certainty.{" "}
        <span style={{ color: "var(--ih-accent)" }}>It asks for attention.</span>
      </p>

      {/* Contributor bio */}
      <div className="ih-rule my-14" />
      <div className="grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-4">
          <img
            src="https://customer-assets.emergentagent.com/job_page-from-feed/artifacts/dafbuybo_Photo%20%282%29.jpg"
            alt="Dr. Michelle Frank"
            className="w-full aspect-square object-cover"
            data-testid="sixthsense-contributor-photo"
          />
        </div>
        <div className="md:col-span-8">
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--ih-accent)" }}>
            Contributor · Issue 001
          </div>
          <h3
            className="mt-3 font-display font-light leading-[1.0] tracking-[-0.01em]"
            style={{ fontSize: "clamp(1.8rem, 3.2vw, 2.6rem)" }}
          >
            Dr. Michelle Frank
          </h3>
          <p className="mt-3 font-manifesto text-[1.15rem] md:text-[1.3rem] leading-[1.35] text-[var(--ih-ink)]">
            Health Consultant &amp; Advisor — Focus Medicine, FemTech, Health &amp; Wellness.
          </p>
          <a
            href="https://www.linkedin.com/in/michelle-frank/"
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.32em] ih-link"
            data-testid="sixthsense-contributor-linkedin"
          >
            LinkedIn <span style={{ color: "var(--ih-accent)" }}>→</span>
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- IN THIS ISSUE ---------- */
const Index = () => (
  <section id="issue" className="py-16 md:py-20 bg-[var(--ih-paper)]" data-reveal>
    <div className="max-w-[1200px] mx-auto px-6 md:px-10">
      <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--ih-accent)" }}>
        Issue 001 · Becoming Her
      </div>
      <div className="flex items-end justify-between gap-6 mt-3 mb-4">
        <h2
          className="font-marquee leading-[0.92] tracking-[-0.01em]"
          style={{ fontSize: "clamp(2.8rem, 8.4vw, 7.6rem)", fontStyle: "normal", fontWeight: 500, fontOpticalSizing: "auto" }}
          data-testid="index-title"
        >
          In This Issue
        </h2>
        <span className="font-marquee italic text-[1.05rem] md:text-[1.25rem] pb-4 text-[var(--ih-ink-soft)]">
          {STORIES.length} stories
        </span>
      </div>
      <div className="ih-rule mb-2" />

      <ul className="divide-y divide-[var(--ih-ink)]/15">
        {STORIES.map((s) => (
          <li key={s.num}>
            <a
              href={s.anchor}
              className="grid grid-cols-12 items-baseline py-8 gap-4 group hover:bg-[var(--ih-cream)] -mx-4 px-4 transition-colors"
              data-testid={`index-row-${s.num}`}
            >
              <span
                className="col-span-2 md:col-span-1 font-marquee italic text-[1.1rem] md:text-[1.3rem]"
                style={{ color: "var(--ih-accent)" }}
              >
                {s.num}
              </span>
              <span
                className="col-span-7 md:col-span-8 font-marquee uppercase tracking-[0.04em] text-[1.4rem] md:text-[2rem] leading-[1.05]"
                style={{ fontStyle: "normal", fontWeight: 500, fontOpticalSizing: "auto" }}
              >
                {s.title}
              </span>
              <span className="col-span-3 md:col-span-2 font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
                {s.chapter}
              </span>
              <span className="hidden md:inline col-span-1 text-right font-mono-ui text-[12px] transition-transform group-hover:translate-x-1" style={{ color: "var(--ih-accent)" }}>
                →
              </span>
            </a>
          </li>
        ))}
      </ul>

      <div className="mt-16 flex items-end justify-between gap-6">
        <p className="font-marquee italic text-[1.15rem] md:text-[1.4rem] text-[var(--ih-ink)]">
          "A magazine for women moving differently."
        </p>
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
          IH · Vol. I
        </span>
      </div>
    </div>
  </section>
);

/* ---------- PULLQUOTE ---------- */
const Pullquote = () => (
  <section className="py-28 md:py-40 bg-[var(--ih-ink)] text-[var(--ih-cream)]" data-reveal>
    <div className="max-w-[1200px] mx-auto px-6 md:px-10">
      <p
        className="font-display font-light leading-[1.02] tracking-[-0.02em]"
        style={{ fontSize: "clamp(1.9rem, 6.8vw, 6.4rem)" }}
        data-testid="pullquote"
      >
        We're not <span className="italic">too much.</span>
        <br />
        You're just <span className="italic" style={{ color: "var(--ih-accent)" }}>not enough.</span>
      </p>
      <div className="mt-10 font-mono-ui text-[10px] uppercase tracking-[0.32em] opacity-70">
        — From the editor's letter, Issue 001
      </div>
    </div>
  </section>
);

/* ---------- THE LISTENING ROOM ---------- */
const Playlist = () => (
  <section id="story-18" className="bg-[var(--ih-ink)] text-[var(--ih-cream)] py-24 md:py-32 border-t border-[var(--ih-cream)]/10" data-reveal>
    <div className="max-w-[1000px] mx-auto px-6 md:px-10">
      <div className="text-center">
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em] opacity-70">
          Issue 001 · 18
        </div>
        <h2
          className="mt-5 font-display font-light leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: "clamp(2.2rem, 5.4vw, 4.8rem)" }}
        >
          The <span className="italic">Listening Room.</span>
        </h2>
        <p className="mt-4 font-display italic text-[1.1rem] md:text-[1.35rem] leading-[1.45] opacity-90">
          Sound for the inner life.
        </p>
        <p className="mt-6 max-w-xl mx-auto text-[0.98rem] md:text-[1rem] leading-[1.7] opacity-75">
          A curated companion to Issue 001 — twelve tracks for women moving differently.
        </p>
      </div>

      <div className="mt-12 overflow-hidden rounded-[12px] border border-[var(--ih-cream)]/15">
        <iframe
          title="The Inner Hustle — Becoming Her, Issue 1"
          src="https://open.spotify.com/embed/playlist/2TQaBLnVXXHUk6CSGLNnYA?utm_source=generator&theme=0"
          width="100%"
          height="380"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{ display: "block" }}
          data-testid="listening-room-spotify"
        />
      </div>

      <div className="mt-12 text-center">
        <a
          href="#issue"
          className="font-mono-ui text-[10px] uppercase tracking-[0.32em] inline-flex items-center gap-2 border-b border-[var(--ih-cream)]/40 pb-[6px] hover:border-[var(--ih-accent)] transition-colors"
          data-testid="listening-room-back-to-issue"
        >
          ← Back to Issue 001
        </a>
      </div>
    </div>
  </section>
);

/* ---------- SUBSCRIBE ---------- */
const Subscribe = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setStatus({ state: "error", msg: "Please enter a valid email." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email });
      if (data?.already_subscribed) {
        setStatus({ state: "success", msg: "You're already in the Circle. Welcome back." });
      } else {
        setStatus({ state: "success", msg: "You're in. Check your inbox — the welcome letter is on its way." });
      }
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went quiet. Try again in a moment.";
      setStatus({ state: "error", msg: detail });
    }
  };

  return (
    <section id="subscribe" className="py-28 md:py-36 bg-[var(--ih-paper)]" data-reveal>
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
        {/* pink dash */}
        <div className="w-12 h-[2px] mx-auto mb-8" style={{ background: "var(--ih-accent)" }} aria-hidden="true" />
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[var(--ih-muted)]">
          Stay Close
        </div>
        <h2
          className="mt-8 font-marquee leading-[1.0] tracking-[-0.01em]"
          style={{ fontSize: "clamp(1.9rem, 6.4vw, 6rem)", fontStyle: "normal", fontWeight: 500, fontOpticalSizing: "auto" }}
          data-testid="subscribe-title"
        >
          Join the inner circle.
        </h2>
        <p className="mt-8 font-manifesto text-[1.1rem] md:text-[1.3rem] leading-[1.5] text-[var(--ih-ink-soft)] max-w-2xl mx-auto">
          No noise. Just the next chapter, when it's ready for you.
        </p>

        <form
          onSubmit={submit}
          noValidate
          className="mt-14 mx-auto max-w-2xl flex items-stretch gap-0"
          data-testid="subscribe-form"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="YOUR EMAIL"
            data-testid="subscribe-input"
            className="flex-1 bg-transparent border-b-2 border-[var(--ih-ink)] focus:border-[var(--ih-accent)] outline-none py-4 px-3 text-[0.95rem] font-mono-ui tracking-[0.18em] placeholder:text-[var(--ih-ink)]/35 transition-colors"
          />
          <button
            type="submit"
            data-testid="subscribe-submit"
            disabled={status.state === "loading"}
            className="flex items-center justify-center gap-3 px-8 md:px-12 py-4 bg-[var(--ih-ink)] text-[var(--ih-cream)] font-mono-ui text-[11px] uppercase tracking-[0.28em] hover:bg-[var(--ih-accent)] transition-colors"
          >
            {status.state === "loading" ? "Sending…" : "Stay Close"}
            <span aria-hidden>→</span>
          </button>
        </form>

        <div className="mt-6 min-h-[1.4rem] font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
          {status.state === "success" && (
            <span style={{ color: "var(--ih-accent-deep)" }} data-testid="subscribe-success">{status.msg}</span>
          )}
          {status.state === "error" && (
            <span style={{ color: "var(--ih-accent-deep)" }} data-testid="subscribe-error">{status.msg}</span>
          )}
          {status.state === "idle" && (
            <span>By subscribing you agree to occasional letters from IH.</span>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- FOOTER ---------- */
const Footer = () => (
  <footer className="bg-[var(--ih-ink)] text-[var(--ih-cream)] pt-20 pb-8" data-testid="ih-footer">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
      <div className="col-span-12 md:col-span-5 min-w-0">
        <img
          src="/ih-logo.png"
          alt="IH — The Inner Hustle"
          className="h-28 md:h-36 w-auto pointer-events-none select-none"
        />
        <div className="mt-2 font-mono-ui text-[10px] uppercase tracking-[0.42em] opacity-80">
          The Inner Hustle
        </div>
        <p className="mt-3 font-display italic text-[1.1rem] opacity-85">
          For women moving differently.
        </p>
      </div>

      <div className="col-span-12 md:col-span-3 min-w-0">
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em] opacity-65 mb-5">
          The Magazine
        </div>
        <ul className="space-y-3 text-[0.98rem]">
          <li><a href="#issue" className="ih-link" data-testid="footer-issue-001">Issue 001</a></li>
          <li><a href="/archive" className="ih-link" data-testid="footer-archive">Archive</a></li>
          <li><a href="#featured" className="ih-link" data-testid="footer-stories">Stories</a></li>
          <li><a href="#manifesto" className="ih-link" data-testid="footer-manifesto">Manifesto</a></li>
          <li><a href="#subscribe" className="ih-link" data-testid="footer-submit">Submit Writing</a></li>
        </ul>
      </div>

      <div className="col-span-12 md:col-span-4 min-w-0">
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.42em] opacity-65 mb-5">
          Find Us
        </div>
        <ul className="space-y-3 text-[0.98rem]">
          <li className="flex items-center gap-3 min-w-0">
            <span aria-hidden className="shrink-0 inline-flex w-6 h-6 items-center justify-center rounded-full border border-[var(--ih-cream)]/40">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
              </svg>
            </span>
            <a href="https://instagram.com/theinnerhustle" target="_blank" rel="noreferrer" className="ih-link min-w-0 break-words" data-testid="footer-instagram">@theinnerhustle</a>
          </li>
          <li className="flex items-center gap-3 min-w-0">
            <span aria-hidden className="shrink-0 inline-flex w-6 h-6 items-center justify-center rounded-full border border-[var(--ih-cream)]/40">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
            </span>
            <a href="mailto:hello@theinnerhustle.life" className="ih-link min-w-0 break-words" data-testid="footer-email">hello@theinnerhustle.life</a>
          </li>
        </ul>
      </div>
    </div>

    <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20 pt-6 border-t border-[var(--ih-cream)]/15 flex flex-col md:flex-row md:items-center md:justify-between gap-3 font-mono-ui text-[10px] uppercase tracking-[0.32em] opacity-70">
      <div>© 2026 The Inner Hustle · All Rights Reserved</div>
      <div>Move Differently.</div>
    </div>
  </footer>
);

/* ---------- PAGE ---------- */
const InnerHustle = ({ scrollTo }) => {
  useReveal();
  useEffect(() => {
    if (!scrollTo) return;
    // Wait for layout, then scroll to the target section
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 20) {
        setTimeout(() => tryScroll(attempts + 1), 50);
      }
    };
    tryScroll();
  }, [scrollTo]);
  return (
    <div className="ih-grain relative" data-testid="inner-hustle-root">
      <Nav />
      <main>
        <Hero />
        {ISSUE_CONTENT_LIVE && (
          <>
            <Marquee />
            <Manifesto />
            <EditorNote />
            <Interlude />
            <Index />
            <Featured />
            <Pullquote />
            <Playlist />
          </>
        )}
        <Subscribe />
      </main>
      <Footer />
    </div>
  );
};

export default InnerHustle;
